import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import { getOperationPalette } from './operationLibraryApi'
import { censusIncludesOperation } from './compositionCensus'
import { CHOICE_KIND } from './libraryEffect'
import { operationRowLines } from './libraryRowLines'
import { contractDurationMs, operationDurationText } from './libraryDuration'
import { failureMessageOf } from './libraryFailure'
import { LibraryBadge, LibraryRow, LibraryScrollList, LibrarySearchField, writeDragData } from './LibraryListParts'
import { useDebouncedTerm } from './usePagedLibraryList'
import OperationVersionControl from './OperationVersionControl'
import OperationUsagesDialog from './OperationUsagesDialog'

/**
 * OperationPaletteSection — the Operations family of the library (story 167; the section of #130/#152).
 *
 * - N6 (`GET operationLibrary/palette?search=`) gives one entry per operation in the OFFERED version (the
 *   highest APPROVED, #152 RN-01.5) and `currentVersionNumber`, which says a newer unapproved one exists. N6
 *   is not paginated: the whole list comes and only scrolls (RN-10.3). The search is the server's.
 * - The census (#152 S73) and the "Pending to add" filter (#152 S82) are derived here from THIS render's
 *   census, on top of the list N6 gave for the search — a change of the model never asks N6 again (J7).
 * - Clicking a row CHOOSES it (D10); the version is changed with the row's control. The chosen version is
 *   per operation and lives while the panel is mounted (#152 S11).
 * - What travels in the drag is only the identity of the version (`nodeId`); N3 reads the content on drop.
 */
const NEVER_REQUESTED = Symbol('never requested')
const EMPTY_PALETTE = { loading: false, failed: false, failureMessage: '', items: [] }

export const operationChoiceOf = (item, version) => ({
  kind: CHOICE_KIND.OPERATION,
  key: 'operation:' + item.code,
  label: item.name || item.code,
  code: item.code,
  versionNumber: version.versionNumber
})

const OperationRowComponent = ({ item, draggedVersion, presentCount, chosen, showDuration, onChooseItem, onVersionChosen, onDragStartItem, onDragEnd, onUsageClick }) => {
  const { t_ } = useSmartTranslation()
  // D9 (C2) + RN-06.10: N6 gives the time of the OFFERED version only; a version chosen by hand paints a dash.
  const offered = draggedVersion.nodeId === item.nodeId
  const durationText = showDuration ? operationDurationText(offered ? contractDurationMs(item.timeMs) : 0) : ''
  // #184 CA1: no `vN` on the secondary line — the version lives in the `vN ▾` control only.
  const lines = operationRowLines(item, durationText)
  // #152 S9: a newer version that is not approved yet is announced with the literal that already says so.
  const hasNewerUnapproved = (item.currentVersionNumber || 0) > (draggedVersion.versionNumber || 0)
  // PO, 29/09/2026 (the mockup's card): its technology, the state of the offered version and in how many
  // workflows the operation already lives.
  // #184 CA2 (RN-02.2, RN-02.3): the usage badge is green and opens the usages dialog — also with 0; with an
  // unknown count (Sun could not read it) there is no badge at all, as before. PO (user testing, 01/10/2026): the
  // green is MIC's `primary` (the outlined look of the `vN ▾` control), not MUI's `success`.
  const facts = [
    { key: 'technology', testId: 'operation-row-technology-' + item.code, text: item.technologyName || '' },
    { key: 'index', testId: 'operation-row-index-' + item.code, text: offered && item.indexValue ? systemValueLabel(t_, 'FeatureGroupRevisionState', item.indexValue) : '' },
    {
      key: 'usage',
      testId: 'operation-row-usage-' + item.code,
      text: Number.isInteger(item.workflowUsageCount) ? t_('Used in {{count}} workflows', { count: item.workflowUsageCount }) : '',
      tone: 'primary',
      onClick: () => onUsageClick(item, draggedVersion.versionNumber)
    }
  ]
  return (
    <LibraryRow
      testId={'operation-row-' + item.code}
      secondaryTestId={'operation-row-secondary-' + item.code}
      primary={lines.primary}
      secondary={lines.secondary}
      icon={HandymanOutlinedIcon}
      facts={facts}
      chosen={chosen}
      draggable
      onClick={() => onChooseItem(item)}
      onDragStart={(event) => onDragStartItem(event, item)}
      onDragEnd={onDragEnd}
      trailing={
        <OperationVersionControl
          code={item.code}
          versionNumber={draggedVersion.versionNumber}
          chosenNodeId={draggedVersion.nodeId}
          onVersionChosen={(version) => onVersionChosen(item, version)}
        />
      }
    >
      {hasNewerUnapproved
        ? <LibraryBadge tone='warning' icon={<NewReleasesOutlinedIcon />} testId={'operation-row-newer-' + item.code}>{t_('New operation version available')}</LibraryBadge>
        : null}
      {/* #152 S73: one node says J9, several say J10 — one key with interpolation, never two glued pieces. */}
      {presentCount === 1
        ? <LibraryBadge tone='success' icon={<CheckCircleOutlineIcon />} testId={'operation-row-present-' + item.code}>{t_('Already in this workflow')}</LibraryBadge>
        : null}
      {presentCount > 1
        ? <LibraryBadge tone='success' icon={<CheckCircleOutlineIcon />} testId={'operation-row-present-' + item.code}>{t_('In this workflow: {{count}}', { count: presentCount })}</LibraryBadge>
        : null}
    </LibraryRow>
  )
}

const OperationRow = React.memo(OperationRowComponent)

const OperationPaletteSection = ({ active = false, census = {}, pendingOnly = false, showDuration = false, chosenKey = '', onChoose, onItemDragStart, onItemDragEnd }) => {
  const { t_ } = useSmartTranslation()
  const [searchText, setSearchText] = useState('')
  const term = useDebouncedTerm(searchText)
  const [palette, setPalette] = useState(EMPTY_PALETTE)
  const [chosenVersions, setChosenVersions] = useState({})
  // #184 CU2: the usages dialog lives in the SECTION, outside the rows (R1). `{}` = closed.
  const [usagesOf, setUsagesOf] = useState({})
  const requestRef = useRef({ term: NEVER_REQUESTED, generation: 0, failed: false })

  useEffect(() => {
    if (!active) {
      return
    }
    if (requestRef.current.term === term && !requestRef.current.failed) {
      return
    }
    const generation = requestRef.current.generation + 1
    requestRef.current = { term, generation, failed: false }
    setPalette((current) => ({ ...current, loading: true }))
    const load = async () => {
      try {
        const items = (await getOperationPalette(term)) || []
        if (requestRef.current.generation === generation) {
          setPalette({ ...EMPTY_PALETTE, items })
        }
      } catch (thrown) {
        if (requestRef.current.generation === generation) {
          requestRef.current = { ...requestRef.current, failed: true }
          setPalette({ ...EMPTY_PALETTE, failed: true, failureMessage: failureMessageOf(thrown) })
        }
      }
    }
    load()
  }, [active, term])

  const draggedVersionOf = useCallback((item) => chosenVersions[item.code] || item, [chosenVersions])

  const chooseItem = useCallback((item) => onChoose(operationChoiceOf(item, draggedVersionOf(item))), [onChoose, draggedVersionOf])

  const chooseVersion = useCallback((item, version) => {
    setChosenVersions((current) => ({ ...current, [item.code]: version }))
    onChoose(operationChoiceOf(item, version))
  }, [onChoose])

  // Stable on purpose: `OperationRow` is memoised and would repaint every row on each render otherwise.
  const openUsages = useCallback((item, versionNumber) => setUsagesOf({ code: item.code, name: item.name || '', versionNumber }), [])
  const closeUsages = useCallback(() => setUsagesOf({}), [])

  const dragItem = useCallback((event, item) => {
    const version = draggedVersionOf(item)
    writeDragData(event, { 'application/reactflow/nodeType': 'operation', 'application/reactflow/subType': version.nodeId })
    onItemDragStart(operationChoiceOf(item, version))
  }, [draggedVersionOf, onItemDragStart])

  const visibleItems = pendingOnly ? palette.items.filter((item) => !censusIncludesOperation(census, item.code)) : palette.items
  // #152 S82 (J13): with the filter on and everything present, the family says so instead of going silent.
  const pendingEmpty = pendingOnly && !palette.loading && !palette.failed && palette.items.length > 0 && visibleItems.length === 0

  return (
    <Stack data-testid='library-operations' spacing={1} sx={{ flex: '1 1 0', minHeight: 0 }}>
      <LibrarySearchField testId='library-search-operations' value={searchText} onChange={setSearchText} placeholder={t_('Search an operation…')} />
      <LibraryScrollList
        testId='library-list-operations'
        loading={palette.loading}
        failed={palette.failed}
        failureText={palette.failureMessage || t_('The operations could not be loaded')}
        empty={!pendingEmpty && visibleItems.length === 0}
      >
        {pendingEmpty
          ? <Typography variant='caption' color='text.secondary' data-testid='library-operations-pending-empty'>{t_('No operations pending to add')}</Typography>
          : null}
        {visibleItems.map((item) => (
          <OperationRow
            key={item.code}
            item={item}
            draggedVersion={draggedVersionOf(item)}
            showDuration={showDuration}
            presentCount={census?.countOf ? census.countOf(item.code) : 0}
            chosen={chosenKey === 'operation:' + item.code}
            onChooseItem={chooseItem}
            onVersionChosen={chooseVersion}
            onDragStartItem={dragItem}
            onDragEnd={onItemDragEnd}
            onUsageClick={openUsages}
          />
        ))}
      </LibraryScrollList>
      <OperationUsagesDialog
        open={!!usagesOf.code}
        code={usagesOf.code || ''}
        name={usagesOf.name || ''}
        selectedVersionNumber={usagesOf.versionNumber || 0}
        onClose={closeUsages}
      />
    </Stack>
  )
}

export default OperationPaletteSection
